import type { TvSnapshot } from './protocol';

/**
 * What the TV draws for a snapshot (TV_REVIVAL_PLAN.md §2.2).
 *
 * - `course`: the 3D green plus the mode HUD.
 * - `mirror`: the phone is on a menu; its choices are shown as a card.
 * - `card`: a round summary or an in-round transition.
 * - `metrics`: a mode without a 3D scene that grades putts (Start Line, or
 *   Target/Distance in their METRICS view) shows big readouts.
 * - `ambient`: connected, nothing running.
 *
 * `snapshot.presentation.kind` wins when the phone sends it. Older app
 * builds publish only `scene`, `menu`, `summary`, and `transition`; the
 * inference below keeps them working.
 */
export type TvPresentationKind = 'course' | 'mirror' | 'card' | 'metrics' | 'ambient';

export function presentationKind(
  snapshot: TvSnapshot | null | undefined,
  options: { freshPutt?: boolean } = {},
): TvPresentationKind {
  if (!snapshot) return 'ambient';
  const hasScene = snapshot.scene?.kind === 'putt';
  const hasCard = Boolean(snapshot.summary || snapshot.transition);
  const declared = snapshot.presentation?.kind;

  if (declared === 'card') return hasCard ? 'card' : hasScene ? 'course' : 'ambient';
  // A summary or transition published over a stale menu mirror (the phone's
  // TvCompanionPage precedes its summary publisher) is a card, not ambient.
  // A round summary also wins over a menu: menus are never drawn (the mirror
  // kind is the start screen), and a menu that leaked from a phone screen in
  // a background tab hid the Distance Challenge summary behind the start
  // screen (Cole, 2026-09-30: "end of a distance challenge provides no
  // stats"). A summary lives only while its phone screen is mounted
  // (useTvSummaryPublisher clears it on unmount).
  if (declared === 'mirror') {
    if (snapshot.summary) return 'card';
    return snapshot.menu ? 'mirror' : hasCard ? 'card' : hasScene ? 'course' : 'ambient';
  }
  if (declared === 'course' || declared === 'replay') {
    if (hasScene) return 'course';
    // The phone says "course" but sent no scene: its METRICS view. Fall
    // through to the inference so the readouts come up.
  }

  if (hasScene) return 'course';
  if (hasCard) return 'card';
  if (metricsMode(snapshot, options.freshPutt === true)) return 'metrics';
  if (snapshot.menu) return 'mirror';
  return 'ambient';
}

/**
 * Start Line always calls for metrics. Target and Distance Challenge call
 * for it only while putts are flowing without a 3D scene (their METRICS
 * view); on their setup menus the mirror or ambient card stays.
 */
export function metricsMode(snapshot: TvSnapshot | null | undefined, freshPutt: boolean): boolean {
  if (!snapshot || snapshot.scene?.kind === 'putt') return false;
  if (snapshot.mode === 'startline') return true;
  return (snapshot.mode === 'target' || snapshot.mode === 'challenge') && freshPutt;
}

/** Footer hint for the phone's sleep policy (§2.3). Null means no footer. */
export function sleepFooter(snapshot: TvSnapshot | null | undefined): string | null {
  return snapshot?.sleep === 'touch' ? 'Grab your device to continue.' : null;
}


/** A putt event older than this no longer grades on the metrics surface. */
export const FRESH_PUTT_WINDOW_MS = 3 * 60_000;

export function isFreshPutt(createdAtMs: number | null | undefined, nowMs: number): boolean {
  return typeof createdAtMs === 'number' && Number.isFinite(createdAtMs) && nowMs - createdAtMs < FRESH_PUTT_WINDOW_MS;
}

/** The phone has been silent for two heartbeat windows. */
export const CONTROLLER_STALE_MS = 100_000;

export function controllerFresh(lastSignalMs: number, nowMs: number): boolean {
  return nowMs - lastSignalMs < CONTROLLER_STALE_MS;
}

/** Title for the mirror and ambient cards; older builds published the
 * retired product name into `title`. */
export function displayTitle(value: string | null | undefined): string {
  const title = value?.trim();
  if (!title) return 'LagKing';
  if (/^(lagking tv|tv display|tvdisplay|lagking studio)$/i.test(title)) return 'LagKing';
  return title;
}
